import Box from '@mui/material/Box';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { LineChart } from '@mui/x-charts/LineChart';
import type { ReactNode } from 'react';
import { MediaCard } from '../media/shared/MediaCard';
import type { MetricTrendData } from './data/api';
import { bucketCountLabel, bucketLabel, bucketTickLabel, formatTrendValue } from './data/format';
import { MetricDelta } from './MetricDelta';

export const CHART_HEIGHT = 200;

// Header row + legend row + card padding around the chart itself — kept in sync with the JSX
// below so `MetricTrendPanel`'s loading skeleton can reserve the same total height and the
// popover doesn't resize once the real chart replaces it.
export const CHART_CARD_HEIGHT = CHART_HEIGHT + 76;
const MAX_TICKS = 7;
const Y_TICK_COUNT = 5;

interface MetricTrendChartProps {
  trend: MetricTrendData;
  currencySymbol: string;
}

function LegendEntry({
  color,
  dashed,
  text,
}: {
  color: string;
  dashed: boolean;
  text: string;
}): ReactNode {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 22,
          borderTopWidth: 2.5,
          borderTopStyle: dashed ? 'dashed' : 'solid',
          borderTopColor: color,
        }}
      />
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
        {text}
      </Typography>
    </Box>
  );
}

export function MetricTrendChart({ trend, currencySymbol }: MetricTrendChartProps): ReactNode {
  const theme = useTheme();
  const buckets = trend.current.series.map((point) => point.bucket);
  const tickStep = Math.max(1, Math.ceil(buckets.length / MAX_TICKS));
  const formatValue = (value: number | null): string =>
    formatTrendValue(trend.name, value, trend.format, currencySymbol);
  const line =
    trend.sentiment === 'bad' ? theme.palette.score.low.main : theme.palette.score.high.main;
  const fill = trend.sentiment === 'bad' ? theme.palette.score.low.bg : theme.palette.score.high.bg;
  const previousLine = alpha(line, 0.45);
  const tickLabelStyle = { fill: theme.palette.text.disabled, fontSize: 11, fontWeight: 500 };
  const xTickLabelStyle = { ...tickLabelStyle, angle: -25, textAnchor: 'end' as const };

  const bucketTooltip = (bucket: number): string => {
    const spans = [trend.current.series[bucket - 1]?.date, trend.previous.series[bucket - 1]?.date]
      .filter((date): date is string => date !== undefined && date !== '')
      .map((date) => bucketLabel(date, trend.granularity));
    return spans.length === 2 ? `${spans[0]} vs ${spans[1]}` : (spans[0] ?? `#${bucket}`);
  };

  return (
    <MediaCard sx={{ px: 2, py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'text.primary' }}>
          {trend.label}
        </Typography>
        <MetricDelta delta={trend.delta} sentiment={trend.sentiment} />
        <Box sx={{ flex: 1 }} />
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'text.disabled' }}>
          {bucketCountLabel(buckets.length, trend.granularity)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
        <LegendEntry color={line} dashed={false} text="This period" />
        <LegendEntry color={previousLine} dashed text="Previous" />
      </Box>
      <LineChart
        height={CHART_HEIGHT}
        margin={{ left: 0, right: 10, top: 8, bottom: 0 }}
        grid={{ horizontal: true }}
        axisHighlight={{ x: 'line' }}
        xAxis={[
          {
            data: buckets,
            scaleType: 'point',
            height: 40,
            disableLine: true,
            disableTicks: true,
            tickLabelStyle: xTickLabelStyle,
            valueFormatter: (bucket: number, context) => {
              if (context.location !== 'tick') return bucketTooltip(bucket);
              const labelled = (bucket - 1) % tickStep === 0 || bucket === buckets.length;
              return labelled ? bucketTickLabel(bucket, trend.granularity) : '';
            },
          },
        ]}
        yAxis={[
          {
            width: 56,
            disableLine: true,
            disableTicks: true,
            tickNumber: Y_TICK_COUNT,
            tickLabelStyle,
            valueFormatter: formatValue,
          },
        ]}
        series={[
          {
            id: 'current',
            data: trend.current.series.map((point) => point.value),
            label: 'This period',
            area: true,
            curve: 'linear',
            color: line,
            showMark: false,
            valueFormatter: formatValue,
          },
          {
            id: 'previous',
            data: trend.previous.series.map((point) => point.value),
            label: 'Previous',
            curve: 'linear',
            color: previousLine,
            shape: 'circle',
            showMark: false,
            valueFormatter: formatValue,
          },
        ]}
        hideLegend
        sx={{
          '& .MuiLineChart-area': { fill, fillOpacity: 1, filter: 'none' },
          '& .MuiLineChart-line': { strokeWidth: 2.5 },
          '& .MuiLineChart-line[data-series-id="previous"]': { strokeDasharray: '6 5' },
          '& .MuiChartsGrid-line': { stroke: theme.palette.divider, strokeOpacity: 0.45 },
          '& .MuiChartsAxisHighlight-root': { stroke: theme.palette.divider },
        }}
      />
    </MediaCard>
  );
}
